import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import Layout from '../components/Layout';
import PageHero from '../components/PageHero';
import SEO from '../components/SEO';
import ContactCTA from '../components/ContactCTA';
import Testimonials from '../components/Testimonials';
import FAQ from '../components/FAQ';
import { cities } from '../data/cities';
import { combineSchemas, organizationSchema, serviceSchema, faqSchema, breadcrumbSchema } from '../lib/schema';
import { MapPin, TrendingUp, Users, Search, Award } from 'lucide-react';

const CityPage = () => {
  const { slug } = useParams();
  const city = cities[slug];

  if (!city) return <Navigate to="/" replace />;

  const path = `/web-design-in-${slug}`;
  const title = `Web Design in ${city.name}, ${city.state} | Website Symphony`;
  const description = `Award-winning custom web design and development in ${city.name}, ${city.state}. We build high-converting websites for local businesses — med spas, tattoo shops, contractors, retailers and more.`;

  const faqs = [
    { q: `How much does a website cost in ${city.name}?`, a: `Custom websites for ${city.name} businesses at Website Symphony typically start at $2,000 for a small informational site and scale to $15,000+ for complex eCommerce or membership platforms. Every project gets a fixed quote up front.` },
    { q: `Do you work with ${city.name} businesses in person?`, a: `Yes. While we serve clients across the U.S. remotely, we regularly meet with ${city.name}-area clients in person for kickoff, brand strategy and photography sessions.` },
    { q: `Can you help my ${city.name} business rank on Google?`, a: `Absolutely. Every site we build for a ${city.name} business ships with local SEO built in: Google Business Profile alignment, service-area pages, schema markup, and page-speed optimization to help you compete in the local pack.` },
    { q: `What industries do you serve in ${city.region}?`, a: `We build websites for med spas, tattoo shops, construction companies, oilfield service firms, medical practices, restaurants, salons, law firms and eCommerce brands across ${city.region}.` },
  ];

  const schema = combineSchemas(
    organizationSchema,
    serviceSchema(`Web Design in ${city.name}`, description, path),
    faqSchema(faqs),
    breadcrumbSchema([{ name: 'Home', path: '/' }, { name: `Web Design in ${city.name}`, path }]),
  );

  return (
    <Layout>
      <SEO
        title={title}
        description={description}
        keywords={`web design ${city.name}, ${city.name} web design, ${city.name} web designer, website design ${city.name}, ${city.name} SEO, local web design ${city.state}`}
        canonical={path}
        schema={schema}
      />
      <PageHero title={`Web Design in ${city.name}, ${city.state}`} subtitle={`Custom websites that help ${city.name}-area businesses stand out, rank higher and convert more.`} breadcrumb={`${city.name} Web Design`} />

      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-[#2C5F66] text-3xl md:text-4xl font-extrabold mb-6">Serving {city.name} and {city.region}</h2>
            <p className="text-gray-600 text-[15px] leading-relaxed mb-4">{city.intro}</p>
            <p className="text-gray-600 text-[15px] leading-relaxed mb-4">Website Symphony builds websites for {city.name} businesses that do one thing exceptionally well: convert visitors into paying customers. Every project starts with strategy, moves through custom design, and ships with local SEO baked in so you rank for the searches that drive real revenue.</p>
            <p className="text-gray-600 text-[15px] leading-relaxed">We also serve neighboring communities including <strong className="text-[#2C5F66]">{city.nearby.join(', ')}</strong>.</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { icon: Users, stat: city.population, label: 'Population served' },
              { icon: Search, stat: 'Top 3', label: 'Local pack goal' },
              { icon: TrendingUp, stat: '2-6 wk', label: 'Typical launch' },
              { icon: Award, stat: '5.0 ★', label: 'Average rating' },
            ].map((s) => (
              <div key={s.label} className="bg-[#F7F7F5] p-6 rounded-lg text-center">
                <s.icon className="mx-auto text-[#D9A04A] mb-3" size={28} />
                <div className="text-[#2C5F66] text-2xl font-extrabold">{s.stat}</div>
                <div className="text-gray-600 text-xs mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-[#F7F7F5]">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-[#2C5F66] text-3xl md:text-4xl font-extrabold text-center mb-4">Industries we serve in {city.name}</h2>
          <p className="text-gray-600 text-center max-w-3xl mx-auto mb-10">Deep experience in the niches that matter most for {city.region} businesses.</p>
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { name: 'Med Spas', to: '/web-design-for-med-spas' },
              { name: 'Tattoo Shops', to: '/web-design-for-tattoo-shops' },
              { name: 'Construction', to: '/web-design-for-construction-companies' },
              { name: 'Oil & Energy', to: '/web-design-for-oil-rig-companies' },
            ].map((i) => (
              <Link key={i.name} to={i.to} className="block bg-white p-6 rounded-lg text-center hover:shadow-lg hover:-translate-y-1 transition-all">
                <MapPin className="mx-auto text-[#2C5F66] mb-3" size={24} />
                <h3 className="text-[#2C5F66] font-bold">{i.name}</h3>
                <p className="text-[#D9A04A] text-xs mt-1 font-semibold">Explore →</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />
      <FAQ items={faqs} title={`${city.name} Web Design FAQs`} />
      <ContactCTA defaultService={`Web Design in ${city.name}`} heading={`Ready to grow your ${city.name} business?`} subheading="Free strategy call and no-obligation quote. Local team, national reach." />
    </Layout>
  );
};

export default CityPage;
